/**
 * ページテクスチャの遅延ライフサイクル（NFR-PERF-01・エンジン共通層）。
 *
 * 表示中の見開きとその近傍だけを保持し、圏外のラスタは close() で解放する。
 * どのページが「近傍」かはエンジンだけが知るため、この層はエンジン側に置く。
 *
 * 1 ページにつき 2 枚まで持つ:
 *   - base   … ページ全面・ズーム非依存。下地とめくりに使う（必ずこれを使う）
 *   - detail … 可視領域だけを高解像度で焼いたもの。ズーム時に base の上へ重ねる
 *
 * detail を「重ねる」形にしているのは、めくり中に base を汚さないため。detail を
 * 消せば必ず正しい絵に戻る（安全側に倒れる）。
 */
import type { PageRaster, PageRasterRequest, PageRegion } from '../core/source';
import { sameRegion } from './region';

interface Slot {
  raster: PageRaster | null;
  /**
   * 要求した幅。プロバイダが上限で切り詰めて小さいラスタを返しても、この値で
   * 再利用を判定する（実測値で判定すると毎フレーム作り直す無限ループに入る）。
   */
  requested: number;
  region: PageRegion | null;
  pending: Promise<PageRaster> | null;
  controller: AbortController | null;
}

interface Entry {
  base: Slot | null;
  detail: Slot | null;
}

/** 既存ラスタで足りるとみなす許容幅（要求幅がこの割合以内なら再ラスタしない）。 */
const REUSE_RATIO = 0.99;

export class TextureStore {
  private readonly entries = new Map<number, Entry>();
  private window = new Set<number>();
  private detailWindow = new Set<number>();

  constructor(private readonly raster: (request: PageRasterRequest) => Promise<PageRaster>) {}

  /** ページ全面のラスタ（下地・めくり用）。ズームでは作り直さない。 */
  get(pageNo: number, targetWidth: number): Promise<PageRaster> {
    return this.fetch(pageNo, 'base', targetWidth, undefined);
  }

  /** 可視領域の高解像度ラスタ。領域か幅が変わったら作り直す。 */
  getDetail(pageNo: number, targetWidth: number, region: PageRegion): Promise<PageRaster> {
    return this.fetch(pageNo, 'detail', targetWidth, region);
  }

  private fetch(
    pageNo: number,
    kind: 'base' | 'detail',
    targetWidth: number,
    region: PageRegion | undefined,
  ): Promise<PageRaster> {
    const entry = this.entries.get(pageNo) ?? { base: null, detail: null };
    this.entries.set(pageNo, entry);

    const slot = entry[kind];
    if (slot && sameRegion(slot.region, region ?? null) && slot.requested >= targetWidth * REUSE_RATIO) {
      if (slot.raster) return Promise.resolve(slot.raster);
      if (slot.pending) return slot.pending;
    }
    if (slot) this.releaseSlot(entry, kind);

    const controller = new AbortController();
    const fresh: Slot = {
      raster: null,
      requested: targetWidth,
      region: region ?? null,
      pending: null,
      controller,
    };
    entry[kind] = fresh;

    fresh.pending = this.raster({ pageNo, targetWidth, region, signal: controller.signal }).then(
      (raster) => {
        // 取得中に差し替え・解放されていたら、そのまま解放して漏らさない。
        if (this.entries.get(pageNo)?.[kind] !== fresh) {
          raster.close();
          return raster;
        }
        fresh.raster = raster;
        fresh.pending = null;
        return raster;
      },
    );
    return fresh.pending;
  }

  /** 保持すべきページ集合を設定し、圏外のページを解放する。 */
  setWindow(pages: Iterable<number>): void {
    this.window = new Set(pages);
    for (const pageNo of [...this.entries.keys()]) {
      if (!this.window.has(pageNo)) this.release(pageNo);
    }
  }

  /** detail を保持するページ（通常は表示中の 1〜2 ページ）。それ以外の detail を解放する。 */
  setDetailWindow(pages: Iterable<number>): void {
    this.detailWindow = new Set(pages);
    for (const [pageNo, entry] of this.entries) {
      if (!this.detailWindow.has(pageNo) && entry.detail) {
        this.releaseSlot(entry, 'detail');
      }
    }
  }

  /** すべての detail を捨てる（めくり開始時など）。 */
  clearDetails(): void {
    this.detailWindow.clear();
    for (const entry of this.entries.values()) {
      if (entry.detail) this.releaseSlot(entry, 'detail');
    }
  }

  /** 保持中のラスタ数（ライフサイクルの検証に使う）。 */
  activeCount(): number {
    let count = 0;
    for (const entry of this.entries.values()) {
      if (entry.base) count++;
      if (entry.detail) count++;
    }
    return count;
  }

  private release(pageNo: number): void {
    const entry = this.entries.get(pageNo);
    if (!entry) return;
    this.releaseSlot(entry, 'base');
    this.releaseSlot(entry, 'detail');
    this.entries.delete(pageNo);
  }

  private releaseSlot(entry: Entry, kind: 'base' | 'detail'): void {
    const slot = entry[kind];
    if (!slot) return;
    entry[kind] = null;
    slot.controller?.abort();
    slot.raster?.close();
    // 取得中のものは then 側で「自分が現役でない」ことを見て解放される。
  }

  dispose(): void {
    for (const pageNo of [...this.entries.keys()]) this.release(pageNo);
    this.window.clear();
    this.detailWindow.clear();
  }
}
